import _ from 'lodash';

import { RATERS } from '@/constants';
import { type HsiycSearchVehiclesRecord } from '@/types/legacy-api/graphql';
import { type AncapTestRecord } from '@/types/ancap';

export const MAX_ANCAP_AGE = 6;

export const OVERRIDES = {
  ANCAP: 'ancap',
  UCSR: 'ucsr',
  COPRESENTATIONAL: 'useCoPresentationProtocol',
};

type GetAncapRatingInput = Pick<AncapTestRecord, 'ratingYear'> & {
  ratingFromAncap: AncapTestRecord['rating'];
  ratingFromRedbook: HsiycSearchVehiclesRecord['ancapRating'];
};

const getAncapRating = ({ ratingFromAncap, ratingFromRedbook, ratingYear }: GetAncapRatingInput) => {
  // prefer redbook-sourced ancap rating, then ancap-sourced rating
  const rating = Number(ratingFromRedbook) || ratingFromAncap;

  const hasValidRating =
    !_.isNull(rating) && rating > 0 && ratingYear >= new Date(Date.now()).getFullYear() - MAX_ANCAP_AGE;

  return {
    provider: RATERS.ANCAP,
    rating,
    hasValidRating,
  };
};

const getUcsrRating = ({ ucsrRating }: Pick<HsiycSearchVehiclesRecord, 'ucsrRating'>) => {
  const hasValidRating = ucsrRating !== undefined && ucsrRating !== null && ucsrRating > 0;

  return {
    provider: RATERS.UCSR,
    rating: ucsrRating,
    hasValidRating,
  };
};

type GetSelectedRatingProviderInput = {
  ratingProviderOverride: HsiycSearchVehiclesRecord['ratingProviderOverride'];
  ancap: ReturnType<typeof getAncapRating>;
  ucsr: ReturnType<typeof getUcsrRating>;
};

const getSelectedRatingProvider = ({ ancap, ucsr, ratingProviderOverride }: GetSelectedRatingProviderInput) => {
  const hasRatingProviderOverride = ratingProviderOverride && ratingProviderOverride !== OVERRIDES.COPRESENTATIONAL;
  const shouldUseAncap = !hasRatingProviderOverride && ancap.hasValidRating;
  const shouldUseUcsr = !hasRatingProviderOverride && ucsr.hasValidRating;

  if ((ratingProviderOverride === OVERRIDES.ANCAP || shouldUseAncap) && ancap.rating) {
    return {
      provider: ancap.provider,
      rating: ancap.rating,
    };
  }

  if ((ratingProviderOverride === OVERRIDES.UCSR || shouldUseUcsr) && ucsr.rating) {
    return {
      provider: ucsr.provider,
      rating: ucsr.rating,
    };
  }

  if (ancap.rating || ucsr.rating) {
    // if there's an ancap rating but no ucsr rating and the ancap rating is old,
    // or has a rating but the rating provider is not what is being displayed
    return {
      provider: RATERS.NO_CURRENT_RATING,
      rating: 0,
    };
  }

  // if there's neither an ancap nor ucsr rating
  return {
    provider: RATERS.NO_RATING,
    rating: 0,
  };
};

const getRating = (vehicle: HsiycSearchVehiclesRecord, ancapTestRecord: AncapTestRecord) => {
  const { ratingProviderOverride, ancapRating: ratingFromRedbook, ucsrRating } = vehicle;
  const { rating: ratingFromAncap, ratingYear } = ancapTestRecord;

  const ancap = getAncapRating({
    ratingFromAncap,
    ratingFromRedbook,
    ratingYear,
  });

  const ucsr = getUcsrRating({
    ucsrRating,
  });

  const selectedRating = getSelectedRatingProvider({
    ancap,
    ucsr,
    ratingProviderOverride,
  });

  return {
    selectedRating,
    ancap,
    ucsr,
  };
};

export { getRating };
